import React from 'react';
import { Box } from '@dropbox/dig-foundations';
import { Text } from '@dropbox/dig-components/typography';
import { UIIcon } from '@dropbox/dig-icons';
import { CheckmarkLine, CloseLine } from '@dropbox/dig-icons/assets';
import { defineMessages, useIntl } from 'react-intl';
import styles from './index.scss';
import type { BillingPlan } from '../types';
import type { FeatureRow } from './helpers';

const messages = defineMessages({
  included: {
    id: 'pricing.featureIncluded',
    description: 'Accessible label for a feature included with a pricing plan',
    defaultMessage: 'Included',
  },
  notIncluded: {
    id: 'pricing.featureNotIncluded',
    description:
      'Accessible label for a feature not included with a pricing plan',
    defaultMessage: 'Not included',
  },
});

interface FeatureValueProps {
  row: FeatureRow;
  plan: BillingPlan;
}

const FeatureValue: React.FC<FeatureValueProps> = ({ row, plan }) => {
  const intl = useIntl();

  if (row.kind === 'check') {
    const included = row.isIncluded(plan);
    return included ? (
      <Box
        color="Primary Base"
        aria-label={intl.formatMessage(messages.included)}
      >
        <UIIcon src={CheckmarkLine} size="medium" />
      </Box>
    ) : (
      <Box
        color="Text Subtle"
        aria-label={intl.formatMessage(messages.notIncluded)}
      >
        <UIIcon src={CloseLine} size="medium" />
      </Box>
    );
  }

  const value = row.getDisplayValue(plan);
  return (
    <Text
      variant="paragraph"
      size="small"
      tagName="span"
      isBold={row.kind === 'main'}
      className={styles.featureValueText}
    >
      {value}
    </Text>
  );
};

export default FeatureValue;
